import './App.css';
import './i18n';
import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Sadaqah from './pages/Sadaqah';
import GoodDeeds from './pages/GoodDeeds';
import Dhikr from './pages/Dhikr';
import Dua from './pages/Dua';
import Bereket from './pages/Bereket';
import BereketStats from './pages/BereketStats';
import Quran from './pages/Quran';
import QuranReader from './pages/QuranReader';
import QuranSearch from './pages/QuranSearch';
import Hadith from './pages/Hadith';
import Hifz from './pages/Hifz';
import PrayerTimes from './pages/PrayerTimes';
import Qibla from './pages/Qibla';
import Journal from './pages/Journal';
import Planner from './pages/Planner';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import { isAdminEmail } from './lib/admin';

const LAST_WEB_ROUTE_KEY = 'bereket_last_web_route';
const PUBLIC_ENTRY_ROUTES = ['/', '/login', '/register'];
const NON_PERSISTED_ROUTES = new Set(['/', '/login', '/register', '/privacy', '/terms', '/auth-callback', '/auth/callback']);
const PWA_INSTALLED_KEY = 'bereket_pwa_installed';
const PWA_PROMPT_MINIMIZED_KEY = 'bereket_pwa_prompt_minimized';

function AuthEntryRoute({ children }) {
  const { user, loading, sessionChecked } = useAuth();

  if (loading || !sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F4] dark:bg-stone-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-stone-600 dark:text-stone-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Register Service Worker
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const SW_REFRESH_VERSION = '2026-03-10-quran-hotfix-1';
      const currentVersion = localStorage.getItem('sw_refresh_version');

      if (currentVersion !== SW_REFRESH_VERSION) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));

        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        }

        localStorage.setItem('sw_refresh_version', SW_REFRESH_VERSION);
      }

      const registration = await navigator.serviceWorker.register(`/service-worker.js?v=${SW_REFRESH_VERSION}`, { updateViaCache: 'none' });
      await registration.update();
      console.log('Service Worker registered:', registration.scope);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
};

// Initialize dark mode from localStorage
const initializeDarkMode = () => {
  const darkMode = localStorage.getItem('darkMode') === 'true';
  if (darkMode) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

function AppRouter() {
  const location = useLocation();
  const { user, loading, sessionChecked } = useAuth();

  useEffect(() => {
    if (!sessionChecked || loading || !user) return;

    const route = `${location.pathname}${location.search}${location.hash}`;
    if (!NON_PERSISTED_ROUTES.has(location.pathname)) {
      localStorage.setItem(LAST_WEB_ROUTE_KEY, route);
    }
  }, [location.hash, location.pathname, location.search, loading, sessionChecked, user]);

  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=') || location.hash?.includes('access_token=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <Routes>
      <Route path="/" element={<AuthEntryRoute><Landing /></AuthEntryRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={<AuthEntryRoute><Login /></AuthEntryRoute>} />
      <Route path="/register" element={<AuthEntryRoute><Register /></AuthEntryRoute>} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sadaqah"
        element={
          <ProtectedRoute>
            {({ user }) => <Sadaqah user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/good-deeds"
        element={
          <ProtectedRoute>
            {({ user }) => <GoodDeeds user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dhikr"
        element={
          <ProtectedRoute>
            {({ user }) => <Dhikr user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dua"
        element={
          <ProtectedRoute>
            {({ user }) => <Dua user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bereket"
        element={
          <ProtectedRoute>
            {({ user }) => <Bereket user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bereket/stats"
        element={
          <ProtectedRoute>
            {({ user }) => <BereketStats user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quran"
        element={
          <ProtectedRoute>
            {({ user }) => <Quran user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quran-reader"
        element={
          <ProtectedRoute>
            {({ user }) => <QuranReader user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quran-search"
        element={
          <ProtectedRoute>
            {({ user }) => <QuranSearch user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/hadith"
        element={
          <ProtectedRoute>
            {({ user }) => <Hadith user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/hifz"
        element={
          <ProtectedRoute>
            {({ user }) => <Hifz user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/prayer-times"
        element={
          <ProtectedRoute>
            {({ user }) => <PrayerTimes user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/qibla"
        element={
          <ProtectedRoute>
            {({ user }) => <Qibla user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            {({ user }) => <Journal user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/planner"
        element={
          <ProtectedRoute>
            {({ user }) => <Planner user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user }) => <Profile user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => (isAdminEmail(user?.email) ? <AdminDashboard user={user} /> : <Navigate to="/dashboard" replace />)}
          </ProtectedRoute>
        }
      />
    </Routes>
    <InstallAppPrompt />
    </>
  );
}

function InstallAppPrompt() {
  const location = useLocation();
  const { user, loading, sessionChecked } = useAuth();
  const { i18n } = useTranslation();
  const isHr = i18n.language === 'hr';

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
    return standalone || localStorage.getItem(PWA_INSTALLED_KEY) === 'true';
  });
  const [isMinimized, setIsMinimized] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(PWA_PROMPT_MINIMIZED_KEY) === 'true';
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setIsMinimized(false);
      localStorage.setItem(PWA_INSTALLED_KEY, 'true');
      localStorage.removeItem(PWA_PROMPT_MINIMIZED_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const canShowPrompt = !loading && sessionChecked && !!user && !PUBLIC_ENTRY_ROUTES.includes(location.pathname) && !isInstalled;

  if (!canShowPrompt) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem(PWA_INSTALLED_KEY, 'true');
        localStorage.removeItem(PWA_PROMPT_MINIMIZED_KEY);
      }
      setDeferredPrompt(null);
      return;
    }

    window.alert(
      isHr
        ? 'Otvorite meni preglednika i odaberite Instaliraj aplikaciju / Dodaj na početni zaslon.'
        : 'Open browser menu and choose Install app / Add to Home Screen.'
    );
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    localStorage.setItem(PWA_PROMPT_MINIMIZED_KEY, 'true');
  };

  const handleRestore = () => {
    setIsMinimized(false);
    localStorage.setItem(PWA_PROMPT_MINIMIZED_KEY, 'false');
  };

  if (isMinimized) {
    return (
      <button
        onClick={handleRestore}
        className="fixed bottom-24 sm:bottom-5 right-4 sm:right-5 z-50 w-12 h-12 rounded-full bg-emerald-600 text-white shadow-xl hover:bg-emerald-700 transition-all"
        data-testid="pwa-install-restore-button"
        title={isHr ? 'Instaliraj aplikaciju' : 'Install app'}
      >
        <Download className="w-5 h-5 mx-auto" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 px-3 pb-3" data-testid="pwa-install-banner">
      <div className="max-w-2xl mx-auto bg-white border border-emerald-200 rounded-2xl shadow-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0" data-testid="pwa-install-banner-text">
          <p className="text-sm font-semibold text-stone-800">{isHr ? 'Instaliraj ovu aplikaciju' : 'Install this app'}</p>
          <p className="text-xs text-stone-600">{isHr ? 'Brže otvaranje, pristup s početnog zaslona i iskustvo kao prava aplikacija.' : 'Faster opening, home screen access, and app-like experience.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMinimize}
            className="px-3 py-2 text-sm rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
            data-testid="pwa-install-minimize-button"
          >
            {isHr ? 'Smanji' : 'Minimize'}
          </button>
          <button
            onClick={handleInstall}
            className="px-3 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5"
            data-testid="pwa-install-cta-button"
          >
            <Download className="w-4 h-4" />
            {isHr ? 'Instaliraj' : 'Install'}
          </button>
          <button
            onClick={handleMinimize}
            className="p-2 rounded-lg text-stone-500 hover:bg-stone-100"
            data-testid="pwa-install-close-button"
            title={isHr ? 'Zatvori' : 'Close'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  // Register service worker and initialize dark mode on app mount
  useEffect(() => {
    registerServiceWorker();
    initializeDarkMode();
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
